import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { PageShell, CardSkeleton, TabBar, Button, ListSkeleton, SkeletonBar } from '../components/ui';
import { T } from '../styles/tokens';

interface Runner {
  id: string;
  name: string;
  status: 'online' | 'offline' | 'disabled';
  server_ip?: string;
  server_region?: string;
  server_provider?: string;
  runner_mode?: string;
  workspace_path?: string;
  workspace_size?: number;
  created_at?: string;
  last_heartbeat_at?: string;
  last_seen_at?: string;
}

// ═══ WhiteLabelClaw Design Tokens ═══
const theme = {
  bg: T.bg,
  bgCard: T.bgInput,
  bgHover: T.bgSecondary,
  bgSelected: '#162040',
  border: T.border,
  borderActive: T.primary,
  blue: T.primary,
  indigo: T.accent,
  white: T.text,
  light: T.textSecondary,
  muted: T.textMuted,
  success: T.success,
  error: T.danger,
  warning: T.warning,
  gradient: 'linear-gradient(135deg, #2F6BFF 0%, #6D4CFF 100%)',
  gradientSubtle: `linear-gradient(135deg, ${T.primaryBg} 0%, ${T.accentBg} 100%)`,
};

export function WorkspaceManager() {
  const [runners, setRunners] = useState<Runner[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRunner, setSelectedRunner] = useState<Runner | null>(null);
  const initWmTab = (() => {
    const seg = window.location.pathname.replace(/^\/workspace-manager\/?/, '').split('/')[0];
    return (['overview', 'permissions'] as const).includes(seg as any) ? seg as 'overview' | 'permissions' : 'overview';
  })();
  const [activeTab, setActiveTabRaw] = useState<'overview' | 'permissions'>(initWmTab);
  const setActiveTab = (t: 'overview' | 'permissions') => {
    setActiveTabRaw(t);
    const newPath = t === 'overview' ? '/workspace-manager' : `/workspace-manager/${t}`;
    if (window.location.pathname !== newPath) window.history.replaceState(null, '', newPath);
  };
  const [permissions, setPermissions] = useState<string[]>([]);
  const [savingPerms, setSavingPerms] = useState(false);
  const [permsSaved, setPermsSaved] = useState(false);

  useEffect(() => { loadRunners(); }, []);

  const loadPermissions = async (runnerId: string) => {
    try {
      const data = await api.get<{ permissions: string[] }>(`/runners/${runnerId}/permissions`);
      setPermissions(data.permissions || []);
    } catch { setPermissions([]); }
  };

  const savePermissions = async () => {
    if (!selectedRunner) return;
    setSavingPerms(true);
    setPermsSaved(false);
    try {
      await api.put<{ permissions: string[] }>(`/runners/${selectedRunner.id}/permissions`, { permissions });
      setPermsSaved(true);
      setTimeout(() => setPermsSaved(false), 3000);
    } catch (e) { console.error('Erro ao salvar permissões:', e); alert('Erro ao salvar permissões'); }
    finally { setSavingPerms(false); }
  };

  const togglePermission = (name: string) => {
    setPermissions(prev => prev.includes(name) ? prev.filter(p => p !== name) : [...prev, name]);
    setPermsSaved(false);
  };

  const loadRunners = async () => {
    try {
      setLoading(true);
      let data: { runners: Runner[] };
      try { data = await api.get<{ runners: Runner[] }>('/workspace/runners'); }
      catch { data = await api.get<{ runners: Runner[] }>('/runners'); }
      setRunners((data.runners || []).filter((r: Runner) => r.status !== 'disabled'));
    } catch (e) { console.error('Erro ao carregar runners:', e); }
    finally { setLoading(false); }
  };

  const getLastSeen = (r: Runner) => r.last_heartbeat_at || r.last_seen_at;

  const isReallyOnline = (r: Runner): boolean => {
    const ls = getLastSeen(r);
    if (!ls) return false;
    return r.status === 'online' && (Date.now() - new Date(ls).getTime()) < 120_000;
  };

  const getHealth = (r: Runner): { icon: string; label: string; color: string } => {
 if (r.status === 'disabled') return { icon: '', label: 'Desabilitado', color: theme.muted };
 if (isReallyOnline(r)) return { icon: '', label: 'Online', color: theme.success };
    const ls = getLastSeen(r);
 if (!ls) return { icon: '', label: 'Nunca conectou', color: theme.muted };
    const diff = Date.now() - new Date(ls).getTime();
 if (diff < 600_000) return { icon: '', label: 'Inativo', color: theme.warning };
 return { icon: '', label: 'Offline', color: theme.error };
  };

  const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const formatBytes = (b?: number) => {
    if (!b) return '0 B';
    const u = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(b) / Math.log(1024));
    return `${(b / Math.pow(1024, i)).toFixed(1)} ${u[i]}`;
  };

  return (
    <div style={{ background: theme.bg, color: theme.white, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <div style={{ padding: '24px 32px', borderBottom: `1px solid ${theme.border}`, background: theme.gradientSubtle }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: theme.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, boxShadow: `0 4px 15px ${T.primaryBgStrong}`,
 }}></div>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.white }}>Workspace Manager</h1>
            <p style={{ margin: 0, fontSize: 12, color: theme.light }}>Gerencie seus executores e permissões</p>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: theme.muted }}>{runners.filter(isReallyOnline).length}/{runners.length} online</span>
            <Button variant="ghost" size="sm" onClick={loadRunners}>↻ Atualizar</Button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', flex: 1, minHeight: 0 }}>
        {/* ═══ Sidebar ═══ */}
        <div style={{ borderRight: `1px solid ${theme.border}`, padding: 16, overflowY: 'auto', minHeight: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: theme.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12, padding: '0 8px' }}>
            Executores ({runners.length})
          </div>

          {loading ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <ListSkeleton rows={3} />
            </div>
          ) : runners.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: theme.muted, fontSize: 13 }}>
 <div style={{ fontSize: 32, marginBottom: 8 }}></div>
              Nenhum executor encontrado
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {runners.map((runner) => {
                const health = getHealth(runner);
                const isSelected = selectedRunner?.id === runner.id;
                const online = isReallyOnline(runner);

                return (
                  <div
                    key={runner.id}
                    onClick={() => { setSelectedRunner(runner); loadPermissions(runner.id); }}
                    style={{
                      padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                      background: isSelected ? theme.bgSelected : 'transparent',
                      border: isSelected ? `1px solid ${theme.borderActive}` : `1px solid transparent`,
                      transition: 'all 0.2s',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={(e: any) => { if (!isSelected) e.currentTarget.style.background = theme.bgHover; }}
                    onMouseLeave={(e: any) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {/* Active indicator */}
                    {isSelected && (
                      <div style={{
                        position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
                        width: 3, height: 20, borderRadius: '0 3px 3px 0',
                        background: theme.gradient,
                      }} />
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {/* Status dot with glow */}
                      <div style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: health.color,
                        boxShadow: online ? `0 0 8px ${health.color}` : 'none',
                        flexShrink: 0,
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, fontWeight: 600, color: theme.white,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{runner.name}</div>
                        <div style={{ fontSize: 11, color: health.color, marginTop: 2, fontWeight: 500 }}>
                          {health.label}
                        </div>
                      </div>
                      <div style={{
                        fontSize: 9, padding: '2px 6px', borderRadius: 4,
                        background: runner.runner_mode === 'openclaw' ? T.primaryBg : T.border,
                        color: runner.runner_mode === 'openclaw' ? theme.blue : theme.muted,
                        fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                      }}>
                        {runner.runner_mode === 'openclaw' ? 'OC' : 'K8s'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ═══ Main Content ═══ */}
        <div style={{ padding: '24px 32px', overflowY: 'auto', minHeight: 0, flex: 1 }}>
          {selectedRunner ? (
            <div>
              {/* Runner header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: isReallyOnline(selectedRunner) ? theme.gradient : theme.bgCard,
                  border: isReallyOnline(selectedRunner) ? 'none' : `1px solid ${theme.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, boxShadow: isReallyOnline(selectedRunner) ? `0 4px 15px ${T.primaryBgStrong}` : 'none',
                }}>
 {isReallyOnline(selectedRunner) ? '' : ''}
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.white }}>{selectedRunner.name}</h2>
                  <div style={{ fontSize: 12, color: theme.muted, marginTop: 4, fontFamily: 'monospace' }}>
                    {selectedRunner.id}
                  </div>
                </div>
                <div style={{
                  padding: '6px 14px', borderRadius: 20,
                  background: isReallyOnline(selectedRunner) ? T.successBg : T.dangerBg,
                  color: getHealth(selectedRunner).color,
                  fontSize: 13, fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: getHealth(selectedRunner).color,
                    boxShadow: isReallyOnline(selectedRunner) ? `0 0 8px ${theme.success}` : 'none',
                  }} />
                  {getHealth(selectedRunner).label}
                </div>
              </div>

              {/* Tabs */}
              <TabBar
                tabs={[
                  { id: 'overview', label: 'Visão Geral' },
                  { id: 'permissions', label: 'Permissões' },
                ]}
                active={activeTab}
                onChange={(id) => setActiveTab(id as 'overview' | 'permissions')}
              />

              {/* ═══ Overview Tab ═══ */}
              {activeTab === 'overview' && (
                <div>
                  {/* Stats grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
                    {[
                      { label: 'Status', value: getHealth(selectedRunner).label, icon: getHealth(selectedRunner).icon, accent: getHealth(selectedRunner).color },
 { label: 'Modo', value: selectedRunner.runner_mode === 'openclaw' ? 'Orquestrador' : 'K8s Workspace', icon: selectedRunner.runner_mode === 'openclaw' ? '' : '', accent: theme.blue },
 { label: 'Região', value: selectedRunner.server_region || 'auto', icon: '', accent: theme.indigo },
 { label: 'IP', value: selectedRunner.server_ip || '0.0.0.0', icon: '', accent: theme.light, mono: true },
 { label: 'Workspace', value: formatBytes(selectedRunner.workspace_size), icon: '', accent: theme.blue },
 { label: 'Criado em', value: formatDate(selectedRunner.created_at), icon: '', accent: theme.muted },
                    ].map((stat, i) => (
                      <div key={i} style={{
                        padding: 16, borderRadius: 12,
                        background: theme.bgCard, border: `1px solid ${theme.border}`,
                        transition: 'all 0.2s',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                          <span style={{ fontSize: 16 }}>{stat.icon}</span>
                          <span style={{ fontSize: 11, color: theme.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{stat.label}</span>
                        </div>
                        <div style={{
                          fontSize: 16, fontWeight: 700, color: stat.accent || theme.white,
                          fontFamily: stat.mono ? 'monospace' : 'inherit',
                        }}>
                          {stat.value}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Last heartbeat */}
                  {getLastSeen(selectedRunner) && (
                    <div style={{
                      padding: 16, borderRadius: 12,
                      background: theme.gradientSubtle, border: `1px solid ${theme.border}`,
                    }}>
                      <div style={{ fontSize: 12, color: theme.muted, marginBottom: 6 }}>Último heartbeat</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: theme.white }}>
                        {formatDate(getLastSeen(selectedRunner)!)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ═══ Permissions Tab ═══ */}
              {activeTab === 'permissions' && (
                <div>
                  <p style={{ fontSize: 13, color: theme.light, marginTop: 0, marginBottom: 20 }}>
                    Selecione quais recursos este executor pode acessar:
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                    {[
 { name: 'workspace', label: 'Workspace', risk: 'green', desc: 'Arquivos e estrutura', icon: '' },
 { name: 'docs', label: 'Documentação', risk: 'green', desc: 'Acessar docs', icon: '' },
 { name: 'memory', label: 'Memória', risk: 'green', desc: 'Histórico e decisões', icon: '' },
 { name: 'config', label: 'Configurações', risk: 'yellow', desc: 'Ler/editar config', icon: '' },
 { name: 'scripts', label: 'Scripts', risk: 'yellow', desc: 'Executar scripts', icon: '' },
 { name: 'agents', label: 'Agentes', risk: 'yellow', desc: 'Gerenciar agentes', icon: '' },
 { name: 'tools', label: 'Ferramentas', risk: 'red', desc: 'Ferramentas de execução', icon: '' },
 { name: 'identity', label: 'Identidade', risk: 'red', desc: 'Acessar identidade', icon: '' },
                    ].map((scope) => {
                      const riskColor = scope.risk === 'red' ? theme.error : scope.risk === 'yellow' ? theme.warning : theme.success;
                      return (
                        <label
                          key={scope.name}
                          style={{
                            display: 'flex', gap: 12, padding: '14px 16px', borderRadius: 10,
                            border: `1px solid ${theme.border}`, cursor: 'pointer',
                            background: theme.bgCard, transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = riskColor; }}
                          onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = theme.border; }}
                        >
                          <input type="checkbox"
                            checked={permissions.includes(scope.name)}
                            onChange={() => togglePermission(scope.name)}
                            style={{
                            cursor: 'pointer', accentColor: theme.blue,
                            width: 16, height: 16, marginTop: 2,
                          }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: theme.white, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span>{scope.icon}</span>
                              {scope.label}
                              <span style={{
                                width: 6, height: 6, borderRadius: '50%',
                                background: riskColor, marginLeft: 4,
                              }} />
                            </div>
                            <div style={{ fontSize: 11, color: theme.muted, marginTop: 4 }}>{scope.desc}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  <Button
                    variant={permsSaved ? 'success' : 'primary'}
                    onClick={savePermissions}
                    loading={savingPerms}
                    style={{ marginTop: 24 }}
                  >
                    {permsSaved ? 'Salvo!' : 'Salvar Permissões'}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            /* Empty state */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 400 }}>
              <div style={{
                width: 80, height: 80, borderRadius: 20,
                background: theme.gradientSubtle, border: `1px solid ${theme.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 36, marginBottom: 20,
 }}></div>
              <h3 style={{ color: theme.white, fontSize: 18, margin: '0 0 8px' }}>Selecione um executor</h3>
              <p style={{ color: theme.muted, fontSize: 13, margin: 0 }}>Escolha um executor na lista para ver detalhes e permissões</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
